import { useState, useEffect, useRef, useCallback } from "react";
import { Bell, Heart, UserPlus, MessageCircle, Flame } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const ICON_MAP = {
  like: Heart,
  reaction: Flame,
  follow: UserPlus,
  comment: MessageCircle,
};

const COLOR_MAP = {
  like: "text-[#FF8FA3]",
  reaction: "text-[#FF6B35]",
  follow: "text-emerald-400",
  comment: "text-blue-400",
};

export const NotificationBell = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await api.get("/notifications/unread-count");
      setUnread(res.data.count);
    } catch (err) { console.error(err); }
  }, [api]);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleOpen = async () => {
    setOpen(!open);
    if (!open) {
      try {
        const res = await api.get("/notifications", { params: { limit: 20 } });
        setNotifs(res.data.notifications);
        if (unread > 0) {
          await api.post("/notifications/read-all");
          setUnread(0);
        }
      } catch (err) { console.error(err); }
    }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}j`;
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={handleOpen}
        className="p-2 rounded-full hover:bg-foreground/5 text-muted-foreground relative"
        data-testid="notification-bell"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 bg-[#FF6B35] text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center"
            data-testid="notification-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed left-3 right-3 top-28 sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80 max-h-[70vh] overflow-y-auto bg-card border border-border rounded-2xl shadow-xl z-50"
          data-testid="notification-panel"
        >
          <div className="p-3 border-b border-border">
            <h3 className="font-heading font-bold text-sm text-card-foreground">Notifications</h3>
          </div>
          {notifs.length === 0 ? (
            <div className="p-6 text-center">
              <Bell className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">Aucune notification</p>
            </div>
          ) : (
            <div>
              <div className="divide-y divide-[#E8E0D8]">
                {notifs.slice(0, 8).map((n) => {
                const Icon = ICON_MAP[n.type] || Bell;
                const color = COLOR_MAP[n.type] || "text-muted-foreground";
                return (
                  <div
                    key={n.id}
                    className={`flex items-start gap-3 p-3 transition-colors ${
                      !n.read ? "bg-[#FF6B35]/5" : ""
                    }`}
                    data-testid={`notification-${n.id}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] flex items-center justify-center shrink-0">
                      {n.actor_avatar ? (
                        <img src={n.actor_avatar} alt="" className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <span className="text-white text-xs font-bold">
                          {(typeof n.actor_name === "string" ? n.actor_name : "?")[0]?.toUpperCase() || "?"}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-card-foreground/80">
                        <span className="font-semibold text-card-foreground">{typeof n.actor_name === "string" ? n.actor_name : n.actor_id || "Systeme"}</span>{" "}
                        {n.content}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(n.created_at)}</p>
                    </div>
                    <Icon className={`w-4 h-4 ${color} shrink-0 mt-0.5`} />
                  </div>
                );
              })}
              </div>
              {notifs.length > 0 && (
                <button
                  onClick={() => { setOpen(false); navigate("/notifications"); }}
                  className="w-full py-2.5 text-center text-xs font-bold text-[#FF6B35] hover:bg-[#FF6B35]/5 border-t border-border transition-colors"
                  data-testid="see-all-notifs-btn"
                >
                  Tout voir
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
